/**
 * QuoteStatusDistribution Component
 *
 * Displays quote distribution pie chart
 */

import { QuoteStatus } from '@/types';
import { Typography, Spin } from 'antd';
import React from 'react';
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import { useQuoteStats } from '../hooks';
import { DateRangeType } from '../types';

const { Title, Text } = Typography;

// Chart colors matching Victorian Sliders branding
const CHART_COLORS: Record<QuoteStatus, string> = {
  [QuoteStatus.Pending]: '#22D3EE',
  [QuoteStatus.Quoted]: '#3B82F6',
  [QuoteStatus.Sent]: '#94A3B8',
  [QuoteStatus.Ordered]: '#10B981',
  [QuoteStatus.PaymentReceived]: '#059669',
  [QuoteStatus.Completed]: '#8B5CF6',
  [QuoteStatus.FollowedUp]: '#F59E0B',
  [QuoteStatus.Issue]: '#EF4444',
};

interface QuoteStatusDistributionProps {
  dateRange?: DateRangeType;
  region?: number;
}

export const QuoteStatusDistribution: React.FC<QuoteStatusDistributionProps> = ({
  dateRange = 'thisweek',
  region,
}) => {
  const { data: quoteStatsData, isLoading } = useQuoteStats(dateRange, region);

  // Map available data from quoteStatsData to distribution format
  const distributionData = React.useMemo(() => {
    if (!quoteStatsData?.overallStats?.current) return [];

    const current = quoteStatsData.overallStats.current;

    const distribution = [
      {
        name: 'Active Quotes',
        value: current.activeQuotes || 0,
        status: QuoteStatus.Quoted,
      },
      {
        name: 'Followed Up',
        value: current.followedUpQuotes || 0,
        status: QuoteStatus.FollowedUp,
      },
      {
        name: 'Converted',
        value: current.convertedQuotes || 0,
        status: QuoteStatus.Ordered,
      },
    ];

    return distribution;
  }, [quoteStatsData]);

  // Filter data for pie chart (only non-zero values)
  const pieChartData = React.useMemo(() => {
    return distributionData.filter((item) => item.value > 0);
  }, [distributionData]);

  return (
    <div className="bg-white rounded-lg p-4 shadow-none">
      <Title level={5} className="!mb-1">
        Quote Status Distribution
      </Title>
      <Text type="secondary" className="text-sm">
        Breakdown of quotes by current status
      </Text>

      {isLoading ? (
        <div className="flex items-center justify-center h-[300px]">
          <Spin />
        </div>
      ) : (
        <>
            <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieChartData}
                cx="50%"
                cy="50%"
                innerRadius={45}
                outerRadius={70}
                paddingAngle={2}
                dataKey="value"
                label={({ name, percent }) =>
                  `${name}: ${(percent * 100).toFixed(0)}%`
                }
              >
                {pieChartData?.map((entry: any, index: number) => (
                  <Cell key={`cell-${index}`} fill={CHART_COLORS[entry.status as QuoteStatus]} />
                ))}
              </Pie>
              <RechartsTooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>

          {/* Stats breakdown showing all categories */}
          <div className="mt-2 grid grid-cols-2 gap-2">
            {distributionData.map((item, index) => (
              <div
                key={index}
                className="flex items-center gap-2 p-1.5 rounded-md bg-gray-50"
              >
                <div
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: CHART_COLORS[item.status as QuoteStatus] }}
                />
                <div className="flex-1 min-w-0">
                  <Text className="text-xs text-gray-600 block truncate">
                    {item.name}
                  </Text>
                  <Text className="text-sm font-semibold block">
                    {item.value}
                  </Text>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default QuoteStatusDistribution;
